import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  standardTransition,
  visuallyHidden,
} from '../styles';
import Search from './Search';
import filterTickIcon from '../images/filterTick-icon.svg';

const StyledSearchFilters = styled.section``;

const StyledInner = styled.div`
  ${minBreakpointQuery.large`
    position: sticky;
    top: 30px;
  `}
`;

const StyledSearch = styled(Search)`
  margin-bottom: 30px;
`;

const StyledFilters = styled.div`
  background-color: ${standardColours.white};
  border-radius: 20px;
  padding: 30px;

  ${minBreakpointQuery.small`
    padding: 40px 35px;
  `}

  ${minBreakpointQuery.large`
    padding: 50px 40px;
  `}
`;

const StyledSubHeading = styled.h3`
  ${fontSize(18)};
  font-weight: ${fontWeights.semibold};
  margin-bottom: 30px;

  ${maxBreakpointQuery.medium`
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;

    
    &:after {
      content: '';
      border: solid black;
      border-width: 0 3px 3px 0;
      display: inline-block;
      padding: 5px;
      transform: ${({ active }) =>
        active ? 'rotate(45deg)' : 'rotate(-135deg)'};
    }

    
    ${({ active }) => {
      if (!active) {
        return css`
          &:last-of-type {
            margin-bottom: 0;
          }
        `;
      }
    }}
  `}

  &:not(:first-child) {
    margin-top: 30px;
  }

  ${minBreakpointQuery.small`
    ${fontSize(20)};
  `}

  ${minBreakpointQuery.medium`
    ${fontSize(22)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(24)};
  `}
`;

const StyledInputWrapper = styled.div`
  display: grid;
  gap: 20px;

  ${maxBreakpointQuery.medium`
    ${({ active }) => {
      if (!active) {
        return css`
          ${visuallyHidden()};
        `;
      }
    }}
  `}

  ${minBreakpointQuery.small`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${minBreakpointQuery.medium`
    flex-direction: column;
    grid-template-columns: 1fr;
  `}
`;

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  appearance: none;
  background-color: #fff;
  margin: 0;
  height: 20px;
  margin-right: 20px;
  position: relative;
  ${fontSize(11)};

  &:before {
    content: '';
    background-color: ${standardColours.white};
    border: solid 1px ${brandColours.secondary};
    font-size: 0;
    padding: 9px;
  }

  &:checked:before {
    background-color: ${brandColours.secondary};
    transition: ${standardTransition('background-color')};
  }

  &:after {
    content: '';
    display: none;
    background: url(${filterTickIcon}) no-repeat;
    height: 8px;
    width: 10px;
    position: absolute;
    top: 55%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:checked:after {
    display: block;
    transition: ${standardTransition('display')};
  }
`;

const StyledInputText = styled.p`
  ${fontSize(16)};

  ${minBreakpointQuery.small`
    ${fontSize(18)};
  `}
`;

const SearchFilters = ({
  searchItems,
  searchResults,
  businessFunctions,
  products,
  popularFeatures,
  filteredResults,
  setSearchResults,
  setFilteredResults,
  location,
  resultsRef,
  ...props
}) => {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [activeFilter, setActiveFilter] = useState();

  const handleFilterClick = name => {
    setActiveFilter(prev => (prev === name ? undefined : name));
  };

  const applyFilters = (itemsToFilter, filters) => {
    if (filters.length === 0) return undefined; // No filters active, return undefined

    // Separate the filters by type
    const productFilters = filters.filter(filter => filter.type === 'product');
    const businessFunctionFilters = filters.filter(
      filter => filter.type === 'businessFunction',
    );
    const popularFeatureFilters = filters.filter(
      filter => filter.type === 'popularFeature',
    );

    const filteredItems = itemsToFilter.filter(item => {
      const itemProducts = item.products.map(product => product.name);
      const itemBusinessFunctions = item.businessFunctions.map(
        businessFunction => businessFunction.name,
      );
      const itemPopularFeatures = item.popularFeatures.map(
        popularFeature => popularFeature.name,
      );

      // Match within each filter category
      const matchesProducts =
        productFilters.length === 0 ||
        productFilters.some(filter => itemProducts.includes(filter.name));

      const matchesBusinessFunctions =
        businessFunctionFilters.length === 0 ||
        businessFunctionFilters.some(filter =>
          itemBusinessFunctions.includes(filter.name),
        );

      const matchesPopularFeatures =
        popularFeatureFilters.length === 0 ||
        popularFeatureFilters.some(filter =>
          itemPopularFeatures.includes(filter.name),
        );

      // Combine the results (AND condition across categories)
      return (
        matchesProducts && matchesBusinessFunctions && matchesPopularFeatures
      );
    });

    // Sort items based on the most recently selected product filter
    if (productFilters.length > 0) {
      filteredItems.sort((a, b) => {
        const latestProductFilter =
          productFilters[productFilters.length - 1]?.name;

        const aHasSpecificProduct = a.products.some(
          product => product.name === latestProductFilter,
        );
        const bHasSpecificProduct = b.products.some(
          product => product.name === latestProductFilter,
        );

        const aIsAllProducts = a.products.length === products.length;
        const bIsAllProducts = b.products.length === products.length;

        // Push "ALL" products items to the bottom first
        if (aIsAllProducts && !bIsAllProducts) return 1;
        if (!aIsAllProducts && bIsAllProducts) return -1;

        // Prioritize items explicitly matching the latest selected product filter
        if (aHasSpecificProduct && !bHasSpecificProduct) return -1;
        if (!aHasSpecificProduct && bHasSpecificProduct) return 1;

        return 0; // Maintain order for items that are equal
      });
    }

    return filteredItems.length > 0 ? filteredItems : null; // Return null if no results
  };

  const toggleFilter = (name, type) => {
    setSelectedFilters(prev => {
      const exists = prev.some(filter => filter.name === name);
      const newFilters = exists
        ? prev.filter(filter => filter.name !== name)
        : [...prev, { name, type }];

      const itemsToFilter = searchResults || searchItems;
      const filteredItems = applyFilters(itemsToFilter, newFilters);

      setFilteredResults(filteredItems); // Set filtered items or null if no results

      if (resultsRef?.current) {
        resultsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }

      return newFilters;
    });
  };

  useEffect(() => {
    if (selectedFilters.length === 0) {
      setFilteredResults(undefined); // No filters active, reset to undefined
    } else if (searchResults !== null) {
      const itemsToFilter = searchResults || searchItems; // Use searchResults if available
      const filteredItems = applyFilters(itemsToFilter, selectedFilters);
      setFilteredResults(filteredItems); // Set filtered items or null if no results
    }
  }, [searchItems, searchResults, selectedFilters, setFilteredResults]);

  return (
    <StyledSearchFilters {...props}>
      <StyledInner>
        <StyledSearch
          items={searchItems}
          setResults={setSearchResults}
          location={location}
          alt={true}
        />
        <StyledFilters>
          <StyledSubHeading
            active={activeFilter === 'businessFunctions'}
            onClick={() => handleFilterClick('businessFunctions')}
          >
            Business function:
          </StyledSubHeading>
          <StyledInputWrapper active={activeFilter === 'businessFunctions'}>
            {businessFunctions.map(({ name }, id) => (
              <StyledLabel key={id}>
                <StyledInput
                  type="checkbox"
                  name="business-functions"
                  onChange={() => toggleFilter(name, 'businessFunction')}
                />
                <StyledInputText>{name}</StyledInputText>
              </StyledLabel>
            ))}
          </StyledInputWrapper>
          <StyledSubHeading
            active={activeFilter === 'products'}
            onClick={() => handleFilterClick('products')}
          >
            Product:
          </StyledSubHeading>
          <StyledInputWrapper active={activeFilter === 'products'}>
            {products.map(({ name }, id) => (
              <StyledLabel key={id}>
                <StyledInput
                  type="checkbox"
                  name="products"
                  onChange={() => toggleFilter(name, 'product')}
                />
                <StyledInputText>{name}</StyledInputText>
              </StyledLabel>
            ))}
          </StyledInputWrapper>
          <StyledSubHeading
            active={activeFilter === 'popularFeatures'}
            onClick={() => handleFilterClick('popularFeatures')}
          >
            Popular features:
          </StyledSubHeading>
          <StyledInputWrapper active={activeFilter === 'popularFeatures'}>
            {popularFeatures.map(({ name }, id) => (
              <StyledLabel key={id}>
                <StyledInput
                  type="checkbox"
                  name="popular-features"
                  onChange={() => toggleFilter(name, 'popularFeature')}
                />
                <StyledInputText>{name}</StyledInputText>
              </StyledLabel>
            ))}
          </StyledInputWrapper>
        </StyledFilters>
      </StyledInner>
    </StyledSearchFilters>
  );
};

export default SearchFilters;
